import React, { useEffect, useState } from "react";
import CountPerLabPerState from "../../api/models/statistics/CountPerLabPerState";
import CountStateChangesPerLabResponse from "../../api/models/statistics/CountStateChangesPerLabResponse";
import StatisticsApi from "../../api/services/StatisticsApi";
import CustomTable from "../../components/CustomTable";
import { LABORATORY_UNDEFINED, TEST_REQUEST_STATE_DISPLAY_NAMES_MAPPING } from "../../constants/display-names";
import useEnv from "../../hooks/env";
import DateRange from "./DateRange.model";

type CSCLab = TestRequestRaw.Schemas.CSCLaboratory;
type ValsOf<T extends Record<string | number | symbol, unknown>> = T[keyof T];
type TestRequestStatusDisplayName = ValsOf<typeof TEST_REQUEST_STATE_DISPLAY_NAMES_MAPPING>;
type Row = {
  [K in TestRequestStatusDisplayName]?: number;
} & { "CSC Laboratory": string; Total?: number; "No. of Requests"?: number };
type RowsByLab = Partial<Record<CSCLab, Row>>;
type TestRequestState = TestRequestRaw.Schemas.TestRequestState;

const {
  __NONE: excludeNone,
  DELETED: excludeDeleted,
  ...REQUEST_STATE_DISPLAY_NAMES
} = TEST_REQUEST_STATE_DISPLAY_NAMES_MAPPING;

interface RequestsTableProps {
  dateRange: DateRange<string> | null;
}

const RequestsTable: React.FC<RequestsTableProps> = ({ dateRange }) => {
  const { api } = useEnv();
  const statisticsApi = new StatisticsApi(api);
  const [requestsDataRows, setRequestsDataRows] = useState<RowsByLab | null>(null);
  const [requestsStatusChangeRows, setRequestsStatusChangeRows] = useState<RowsByLab | null>(null);

  const createRequestsDataRows = (requestsData: CountPerLabPerState[]) => {
    const rows = requestsData.reduce<RowsByLab>((byLab, { count, laboratory, testRequestState }) => {
      if (testRequestState !== "DELETED" && testRequestState !== "__NONE") {
        const prevTotal = byLab[laboratory?.key || "_UNDEFINED"]?.Total || 0;
        byLab[laboratory?.key || "_UNDEFINED"] = {
          ...(byLab[laboratory?.key || "_UNDEFINED"] || {}),
          [REQUEST_STATE_DISPLAY_NAMES[testRequestState]]: count,
          "CSC Laboratory": laboratory?.name || LABORATORY_UNDEFINED,
          Total: count ? prevTotal + count : prevTotal,
        };
      }
      return byLab;
    }, {});
    return rows;
  };

  const mapStateChangesCount = (stateChangesCount: Record<TestRequestState, number>) => {
    return (Object.keys(stateChangesCount) as TestRequestState[]).reduce<Record<TestRequestStatusDisplayName, number>>(
      (translated, currState) => {
        if (currState !== "DELETED" && currState !== "__NONE") {
          translated[REQUEST_STATE_DISPLAY_NAMES[currState]] = stateChangesCount[currState];
          return translated;
        }
        return translated;
      },
      {} as Record<TestRequestStatusDisplayName, number>,
    );
  };

  const createRequestsStatusChangeRows = (stateChangesResponse: CountStateChangesPerLabResponse[]) => {
    const rows = stateChangesResponse.reduce<RowsByLab>((byLab, { count, laboratory, stateChanges }) => {
      const prevTotal = byLab[laboratory?.key || "_UNDEFINED"]?.Total || 0;
      byLab[laboratory?.key || "_UNDEFINED"] = {
        ...(byLab[laboratory?.key || "_UNDEFINED"] || {}),
        ...mapStateChangesCount(stateChanges as Record<TestRequestState, number>),
        "CSC Laboratory": laboratory?.name || LABORATORY_UNDEFINED,
        "No. of Requests": count ? prevTotal + count : prevTotal,
      };
      return byLab;
    }, {});
    return rows;
  };

  useEffect(() => {
    statisticsApi.fetchTestRequestCountPerLabAndState().then(({ data }) => {
      setRequestsDataRows(createRequestsDataRows(data));
    });
  }, []);

  useEffect(() => {
    if (dateRange) {
      statisticsApi.fetchTestRequestStateChangeCountPerLab(dateRange).then(({ data }) => {
        const rows = createRequestsStatusChangeRows(data);
        setRequestsStatusChangeRows(rows);
      });
    }
  }, [dateRange]);

  return (
    requestsDataRows && (
      <>
        <CustomTable
          headDense
          rowOrderFixed
          boldIndex={0}
          columns={["CSC Laboratory", ...Object.values(REQUEST_STATE_DISPLAY_NAMES)].concat([
            dateRange ? "No. of Requests" : "Total",
          ])}
          fillEmpty={0}
          rows={
            dateRange && requestsStatusChangeRows
              ? Object.values(requestsStatusChangeRows)
              : Object.values(requestsDataRows)
          }
        />
      </>
    )
  );
};

export default RequestsTable;
